import { useState } from "react";
import { Link } from "react-router-dom";

import styles from "./RepoSwitcher.module.css";
import { Button } from "../components/Button";
import { Pill } from "../components/Pill";
import { Popover, PopoverContent, PopoverTrigger } from "../components/Popover";
import { RepoAvatar } from "../components/RepoAvatar";
import { repoLabel, useRepositories } from "../data/api/repositories";
import { Icon } from "../icons";
import { cx } from "../lib/cx";
import { paths } from "../routing/paths";

export type RepoSwitcherProps = {
  readonly currentRepoId?: string;
};

export const RepoSwitcher = ({ currentRepoId }: RepoSwitcherProps) => {
  const { repositories } = useRepositories();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const current = repositories.find((r) => r.id === currentRepoId);
  const filtered = repositories.filter((r) =>
    repoLabel(r).toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button className={styles.trigger} size="sm" variant="ghost">
          <Icon.Folder size={14} />
          <span className={styles.triggerLabel}>
            {current ? repoLabel(current) : "Pick a repo"}
          </span>
          <Icon.ChevDown size={14} style={{ opacity: 0.5 }} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={styles.dd} sideOffset={4}>
        <div className={styles.head}>
          <input
            autoFocus
            className={styles.search}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a repository…"
            value={query}
          />
        </div>
        <div className={styles.list}>
          {filtered.map((r) => {
            const running = r.agents.running;
            return (
              <Link
                className={cx(
                  styles.item,
                  r.id === currentRepoId && styles.active,
                )}
                key={r.id}
                onClick={() => setOpen(false)}
                to={paths.repo(r.id)}
              >
                <RepoAvatar org={r.org} radius={5} size={22} />
                <div style={{ minWidth: 0 }}>
                  <div className={styles.name}>{r.name}</div>
                  <div className={styles.org}>{r.org}</div>
                </div>
                <span className={styles.spacer} />
                {running > 0 && <Pill status="running">{running}</Pill>}
              </Link>
            );
          })}
          {filtered.length === 0 && (
            <div className={styles.empty}>No matches.</div>
          )}
        </div>
        <div className={styles.foot}>
          <Link
            className={styles.footBtn}
            onClick={() => setOpen(false)}
            to={paths.settings()}
          >
            <Icon.Plus size={14} /> Connect a repository in Settings
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};
